import React, { useEffect, useState, useRef, useMemo } from "react";
import CreateShiftModal from "../components/modals/CreateShiftModal.js";
import { getServiceUsers, getStaffMembers } from "../services/users.js";
import { getShifts, deleteShift } from "../services/shifts.js";
import {
  capitalize,
  formatTotalShiftHours,
  formatUKTime,
  totalHours,
  convertUTCToLocal,
  toUKWallTime,
} from "../utils/functions.js";
import { getShiftNoteText } from "../utils/shiftNote.js";
import { isMedicationApplicable } from "../utils/medicationStatus.js";
import DateFilter from "../components/Forms/DateFilter.js";
import { BasicSelect } from "../components/Forms/BasicSelect.js";
import { FaRegClone } from "react-icons/fa";
import ReusableTable from "../components/ReusableTable.js";
import { useSearchParams } from "react-router-dom";
import { getConfig } from "../services/util.js";
import { StatusBadge } from "../components/StatusBadge.js";
import { Link } from "react-router-dom";
import { useTheme } from "../context/ThemeContext.js";
import { DeleteIcon } from "../components/icons/DeleteIcon.jsx";
import toast from "react-hot-toast";


const ShifteTableAction = ({
  shift,
  setIsModalOpen,
  setInitialShift,
  setIsCloning,
  onDelete,
}) => {
  const cloneShift = () => {
    const newShift = {
      shiftType: shift.shiftType,
      serviceUserId: shift.serviceUser.id,
      staffId: shift.staff.id,
      call: shift.call,
    };
    setInitialShift(newShift);
    setIsCloning(true);
    setIsModalOpen(true);
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete shift #${shift.shiftId}? This action cannot be undone.`)) {
      onDelete(shift.shiftId);
    }
  };

  return (
    <div className="user__table-action">
      <button className="action-btn action-btn--delete" onClick={handleDelete} data-tooltip="Delete shift">
        <DeleteIcon />
      </button>
      <button onClick={cloneShift} title="Clone shift">
        <FaRegClone
          className="user__table-action--icon"
          size="20"
        />
      </button>
    </div>
  );
};

export default function Shifts() {
  const { theme } = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [shifts, setShifts] = useState([]);
  const [initialShift, setInitialShift] = useState(null);
  const [isCloning, setIsCloning] = useState(false);
  const [staffOpt, setStaffOpt] = useState([]);
  const [serviceUserOpt, setServiceUserOpt] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedServiceUser, setSelectedServiceUser] = useState(null);
  const [filteredShifts, setFilteredShifts] = useState([]);
  const [sortDuration, setSortDuration] = useState({
    label: "Today",
    value: "today",
  });
  const [disableFilterApply, setDisableFilterApply] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [totalShiftHours, setTotalShiftHours] = useState(0);
  const [totalClockedHours, setTotalClockedHours] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial state from URL (defaults if not set)
  const pageIndex = useMemo(() => {
    const pageParam = searchParams.get("page");
    const page = parseInt(pageParam, 10);
    return isNaN(page) ? 0 : Math.max(0, page);
  }, [searchParams]);
  const sortOptions = [
    {
      label: "Today",
      value: "today",
    },
    {
      label: "This Week",
      value: "week",
    },
    {
      label: "This Month",
      value: "month",
    },
  ];

  const columns = useMemo(
    () => [
      {
        accessorKey: "shiftId",
        header: "Shift ID",
        cell: ({ row }) => (
          <Link to={`/shift/${row.original.shiftId}`}>
            {row.original.shiftId}
          </Link>
        ),
        sortingFn: (rowA, rowB) => {
          const idA = parseInt(rowA.original.shiftId, 10);
          const idB = parseInt(rowB.original.shiftId, 10);
          return idA - idB;
        },
      },
      {
        accessorKey: "medicationStatus",
        header: "Med Status",
        cell: ({ row }) => {
          const hasMedications = row.original.hasMedicationsAdministered;
          const hasPrn = row.original.hasPrnAdministered;
          const isAdministered = hasMedications || hasPrn;
          const status = row.original.status?.toLowerCase();
          const isCompleted = status === "completed";
          const isLate = status === "late";
          const isMissed = status === "missed";

          // CCS-379: never show a medication status icon for shifts where
          // medication is not applicable (no medication group assigned and
          // nothing administered) — otherwise a completed shift with no
          // medication looks like a missed dose.
          if (!isMedicationApplicable(row.original)) {
            return <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}></div>;
          }

          // Show nothing if not in relevant status and no medications administered
          if (!isCompleted && !isLate && !isMissed && !isAdministered) {
            return <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}></div>;
          }

          // For LATE/MISSED shifts with medications administered, show only exclamation mark
          if ((isLate || isMissed) && isAdministered) {
            const isDarkTheme = theme === 'dark';
            return (
              <div className="pill-container" style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "44px", position: "relative" }}>
                <div
                  style={{
                    width: "14px",
                    height: "14px",
                    borderRadius: "50%",
                    backgroundColor: isDarkTheme ? "rgba(235,87,87,0.15)" : "#fdeaea",
                    border: `1px solid ${isDarkTheme ? "#ec7063" : "#eb5757"}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "9px",
                    fontWeight: "bold",
                    color: isDarkTheme ? "#ec7063" : "#eb5757",
                    position: "absolute",
                    left: "15px"
                  }}
                  title={isLate ? "Shift was late but medications administered" : "Shift was missed but medications administered"}
                >
                  !
                </div>
              </div>
            );
          }

          // Determine pill color based on medication status and theme
          const isDarkTheme = theme === 'dark';

          let pillConfig;

          if (isAdministered) {
            pillConfig = {
              background: isDarkTheme ? "rgba(59,173,84,0.15)" : "#ebf7ee",
              border: isDarkTheme ? "#58d68d" : "#3bad54",
              shadow: "rgba(59,173,84,0.12)",
              centerLine: isDarkTheme ? "#58d68d" : "#3bad54",
              title: "Medications administered"
            };
          } else if (isLate || isMissed) {
            pillConfig = {
              background: isDarkTheme ? "rgba(235,87,87,0.15)" : "#fdeaea",
              border: isDarkTheme ? "#ec7063" : "#eb5757",
              shadow: "rgba(235,87,87,0.12)",
              centerLine: isDarkTheme ? "#ec7063" : "#eb5757",
              title: isLate ? "Shift late - medications not administered" : "Shift missed - medications not administered"
            };
          } else {
            pillConfig = {
              background: isDarkTheme ? "rgba(235,87,87,0.15)" : "#fdeaea",
              border: isDarkTheme ? "#ec7063" : "#eb5757",
              shadow: "rgba(235,87,87,0.12)",
              centerLine: isDarkTheme ? "#ec7063" : "#eb5757",
              title: "Shift completed - No medications administered"
            };
          }

          return (
            <div className="pill-container" style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", width: "44px", position: "relative" }}>
              {/* Fixed position for pill - always in the same spot */}
              <div
                style={{
                  width: "12px",
                  height: "24px",
                  borderRadius: "12px",
                  background: `linear-gradient(135deg, ${pillConfig.background} 0%, ${pillConfig.background} 50%, rgba(0,0,0,0.03) 100%)`,
                  border: `1px solid ${pillConfig.border}`,
                  position: "absolute",
                  left: "0",
                  display: "inline-block",
                  boxShadow: `
                    0 1px 2px rgba(0,0,0,0.1),
                    inset 0 1px 0 rgba(255,255,255,0.3),
                    inset 0 -1px 0 rgba(0,0,0,0.05)
                  `,
                  cursor: "default"
                }}
                title={pillConfig.title}
              >
                {/* Center score line */}
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "1px",
                    right: "1px",
                    height: "0.5px",
                    backgroundColor: pillConfig.centerLine,
                    transform: "translateY(-50%)",
                    opacity: 0.6
                  }}
                />
                {/* Subtle surface reflection */}
                <div
                  style={{
                    position: "absolute",
                    top: "1px",
                    left: "3px",
                    right: "3px",
                    height: "3px",
                    background: "linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.1) 100%)",
                    borderRadius: "8px",
                    opacity: 0.7
                  }}
                />
              </div>
              {/* Warning indicator for red pills - positioned next to pill */}
              {!isAdministered && (
                <div
                  style={{
                    width: "14px",
                    height: "14px",
                    borderRadius: "50%",
                    backgroundColor: pillConfig.border,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "absolute",
                    left: "30px",
                    boxShadow: `0 1px 2px rgba(0,0,0,0.15)`,
                    cursor: "default"
                  }}
                  title="Attention: No medications administered"
                >
                  <span
                    style={{
                      fontSize: "9px",
                      fontWeight: "700",
                      color: "white",
                      lineHeight: "1",
                      fontFamily: "system-ui, -apple-system, sans-serif"
                    }}
                  >
                    !
                  </span>
                </div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "serviceUser",
        header: "Service User",
        cell: ({ row }) =>
          `${row.original.serviceUser.firstName} ${row.original.serviceUser.lastName}`,
        sortingFn: (rowA, rowB) => {
          const nameA =
            `${rowA.original.serviceUser.firstName} ${rowA.original.serviceUser.lastName}`.toLowerCase();
          const nameB =
            `${rowB.original.serviceUser.firstName} ${rowB.original.serviceUser.lastName}`.toLowerCase();
          return nameA.localeCompare(nameB);
        },
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        cell: ({ row }) =>
          formatUKTime(row.original.createdAt, "dd/MM/yyyy - HH:mm"),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        accessorKey: "shiftStart",
        header: "Shift Start",
        cell: ({ row }) =>
          formatUKTime(row.original.shiftStart, "dd/MM/yyyy - HH:mm"),
      },
      {
        accessorKey: "shiftEnd",
        header: "Shift End",
        cell: ({ row }) =>
          formatUKTime(row.original.shiftEnd, "dd/MM/yyyy - HH:mm"),
      },
      {
        accessorKey: "totalHours",
        header: "Duration",
        cell: ({ row }) => {
          const { hours, minutes } = totalHours(
            convertUTCToLocal(row.original.shiftStart),
            convertUTCToLocal(row.original.shiftEnd)
          );
          return `${hours}h ${minutes}m`;
        },
      },
      {
        accessorKey: "clockInTime",
        header: "Clock In Time",
        cell: ({ row }) => formatUKTime(row.original.clockedIn, "dd/MM/yyyy - HH:mm"),
      },
      {
        accessorKey: "clockOutTime",
        header: "Clock Out Time",
        cell: ({ row }) => formatUKTime(row.original.clockedOut, "dd/MM/yyyy - HH:mm"),
      },
      {
        accessorKey: "totalClockedHours",
        header: "Clocked Duration",
        cell: ({ row }) => {
          const { hours, minutes } = totalHours(
            convertUTCToLocal(row.original.clockedIn),
            convertUTCToLocal(row.original.clockedOut)
          );
          return `${hours}h ${minutes}m`;
        },
      },
      {
        accessorKey: "Staff",
        // header: "Staff",
        cell: ({ row }) =>
          `${row.original.staff.firstName} ${row.original.staff.lastName}`,
        sortingFn: (rowA, rowB) => {
          const nameA =
            `${rowA.original.staff.firstName} ${rowA.original.staff.lastName}`.toLowerCase();
          const nameB =
            `${rowB.original.staff.firstName} ${rowB.original.staff.lastName}`.toLowerCase();
          return nameA.localeCompare(nameB);
        },
      },
      { accessorKey: "shiftType", header: "Shift Type" },
      {
        accessorKey: "call",
        header: "Call",
        cell: ({ row }) => `${capitalize(row.original.call)}`,
      },
      {
        accessorKey: "notes",
        header: "Notes",
        cell: ({ row }) => getShiftNoteText(row),
      },
      // {
      //   accessorKey: "administered",
      //   header: "Medication",
      //   cell: ({ row }) => {
      //     if (!row.original.administered) return "";
      //     return row.original.administered
      //       .split(";")
      //       .map((medication) => capitalize(medication))
      //       .join(", ");
      //   },
      // },
      {
        accessorKey: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <ShifteTableAction
            shift={row.original}
            setIsModalOpen={setIsModalOpen}
            setInitialShift={setInitialShift}
            setIsCloning={setIsCloning}
            onDelete={handleDeleteShift}
          />
        ),
      },
    ],
    [theme]
  );

  const dateFilterRef = useRef();

  const handleDisableFilterButton = () => {
    if (
      dateRange !== null ||
      dateRange?.startDate === "" ||
      dateRange?.endDate === "" ||
      selectedStaff !== null ||
      selectedServiceUser !== null
    ) {
      setDisableFilterApply(false);
    } else {
      setDisableFilterApply(true);
    }
  };

  const fetchConfig = async () => {
    try {
      const { data } = await getConfig();
      localStorage.setItem("config", JSON.stringify(data));
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    handleDisableFilterButton();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStaff, selectedServiceUser, dateRange]);

  const passQuery = async () => {
    const query = {
      staffId: selectedStaff?.value,
      serviceUserId: selectedServiceUser?.value,
      shiftStart: dateRange?.startDate,
      shiftEnd: dateRange?.endDate,
      limit: 1000,
    };
    await fetchShifts(query);
    setSortDuration({ label: "Custom", value: "" });
  };

  const sortByNearestDate = (shifts) => {
    const currentDate = new Date();

    return shifts.sort((a, b) => {
      const dateA = new Date(a.shiftStart);
      const dateB = new Date(b.shiftStart);

      // Calculate the absolute difference from the current date
      const diffA = Math.abs(dateA - currentDate);
      const diffB = Math.abs(dateB - currentDate);

      // Sort based on the difference
      return diffA - diffB;
    });
  };

  const fetchShifts = async (query) => {
    setIsLoading(true);
    try {
      const cachedShifts = localStorage.getItem("shifts");
      if (cachedShifts) {
        const cachedData = JSON.parse(cachedShifts);
        setShifts(cachedData);
      }
      const { data } = await getShifts(query);
      setShifts(sortByNearestDate(data));

      localStorage.setItem("shifts", JSON.stringify(data));

      fetchConfig(); // Fetch and store config data
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchServiceUser = async () => {
    try {
      const { data } = await getServiceUsers();
      if (data) {
        const options = data.map((user) => {
          return {
            label: `${user.firstName} ${user.lastName}`,
            value: user.id,
          };
        });
        setServiceUserOpt(options);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchStaffMembers = async () => {
    try {
      const { data } = await getStaffMembers();
      if (data) {
        const options = data.map((user) => {
          return {
            label: `${user.firstName} ${user.lastName}`,
            value: user.staffId,
          };
        });
        setStaffOpt(options);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    Promise.all([
      fetchServiceUser(),
      fetchStaffMembers(),
      fetchShifts({ staffId: "", limit: 1000 }),
    ]);
    // eslint-disable-next-line
  }, []);
  const handleDateFilterChange = (range) => {
    if (range) {
      setDateRange(range);
    }
  };

  const handleUserChange = (e, name) => {
    if (e) {
      setSelectedStaff(name === "staff" ? e : selectedStaff);
      setSelectedServiceUser(name === "serviceUser" ? e : selectedServiceUser);
    }
  };

  useEffect(() => {
    if (selectedStaff || selectedServiceUser) {
      passQuery();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStaff, selectedServiceUser]);

  const clearFilter = () => {
    setSelectedStaff(null);
    setSelectedServiceUser(null);
    setDateRange(null);
    setSortDuration({ label: "Today", value: "today" });
    if (dateFilterRef.current) {
      dateFilterRef.current.handleClearFilter(); // Call the function from the child
    }
  };

  useEffect(() => {
    if (sortDuration && shifts) {
      let sorted;
      // Bucket by UK calendar day so "today"/"week"/"month" mean the same
      // thing on every device, regardless of its timezone
      if (sortDuration.value === "today") {
        sorted = shifts.filter((shift) => {
          const shiftDate = toUKWallTime(shift.shiftStart);
          const today = toUKWallTime(new Date());
          if (!shiftDate) return false;
          const sameDay =
            shiftDate.getDate() === today.getDate() &&
            shiftDate.getMonth() === today.getMonth() &&
            shiftDate.getFullYear() === today.getFullYear();
          return sameDay;
        });
      } else if (sortDuration.value === "week") {
        sorted = shifts.filter((shift) => {
          const shiftDate = toUKWallTime(shift.shiftStart);
          const today = toUKWallTime(new Date());
          if (!shiftDate) return false;
          const sameWeek =
            shiftDate.getFullYear() === today.getFullYear() &&
            shiftDate.getMonth() === today.getMonth() &&
            shiftDate.getDate() >= today.getDate() - 7;
          return sameWeek;
        });
      } else if (sortDuration.value === "month") {
        sorted = shifts.filter((shift) => {
          const shiftDate = toUKWallTime(shift.shiftStart);
          const today = toUKWallTime(new Date());
          if (!shiftDate) return false;
          const sameMonth =
            shiftDate.getFullYear() === today.getFullYear() &&
            shiftDate.getMonth() === today.getMonth();
          return sameMonth;
        });
      } else {
        sorted = shifts;
      }
      setTotalShiftHours(formatTotalShiftHours(sorted));
      setTotalClockedHours(formatTotalShiftHours(sorted, "clocked"));
      setFilteredShifts(sorted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortDuration, shifts]);

  const updatePage = (newPageIndex) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("page", newPageIndex);
      return params;
    });
  };

  const reload = () => {
    if (selectedStaff || selectedServiceUser || dateRange) {
      fetchShifts({
        staffId: selectedStaff?.value || "",
        serviceUserId: selectedServiceUser?.value || "",
        shiftStart: dateRange?.startDate,
        shiftEnd: dateRange?.endDate,
        limit: 1000,
      });
    } else {
      fetchShifts({ staffId: "", limit: 1000 });
    }
  };

  const handleDeleteShift = async (shiftId) => {
    setIsDeleting(true);
    try {
      await deleteShift(shiftId);
      toast.success("Shift deleted successfully");
      // Remove the deleted shift from local state
      setShifts(prevShifts => prevShifts.filter(shift => shift.shiftId !== shiftId));
      setFilteredShifts(prevFiltered => prevFiltered.filter(shift => shift.shiftId !== shiftId));
    } catch (error) {
      toast.error(error.message || "Failed to delete shift");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="shifts-container" data-theme={theme}>
      <div className="user__top">
        <div>
          <button
            className="btn btn__primary"
            onClick={() => {
              setIsModalOpen(true);
              setInitialShift(null);
            }}
          >
            + Create shift
          </button>
        </div>
      </div>
      <div className="d-flex flex-wrap j-c-sb mb-16" style={{ gap: "10px" }}>
        <div className="filter__container">
          <BasicSelect
            placeholder="Filter by staff"
            options={staffOpt}
            name="staff"
            value={selectedStaff}
            onChange={(e) => handleUserChange(e, "staff")}
            isSearchable={true}
          />
          <BasicSelect
            placeholder="Filter by service user"
            options={serviceUserOpt}
            name="serviceUser"
            value={selectedServiceUser}
            onChange={(e) => handleUserChange(e, "serviceUser")}
            isSearchable={true}
          />
          <DateFilter
            onFilterChange={handleDateFilterChange}
            ref={dateFilterRef}
          />

          <div className="d-flex">
            <button
              className="btn btn--secondary"
              style={{ height: "37px" }}
              disabled={disableFilterApply}
              onClick={passQuery}
            >
              Apply filter
            </button>
            {!disableFilterApply && (
              <button
                className="btn btn__link ml-4"
                style={{ height: "37px", fontSize: "12px" }}
                onClick={() => {
                  clearFilter();
                  fetchShifts({ staffId: "", limit: 1000 });
                }}
              >
                Clear filter
              </button>
            )}
          </div>
        </div>
        <BasicSelect
          placeholder="Select a date range"
          options={sortOptions}
          name="staff"
          value={sortDuration}
          width="200px"
          onChange={(e) => setSortDuration(e)}
        />
      </div>

      <ReusableTable
        data={filteredShifts}
        columns={columns}
        isLoading={isLoading || isDeleting}
        pageIndex={pageIndex}
        emptyText="No shifts found"
        onPageChange={updatePage}
      />

      <ul className="total-shifthours">
        <li>
          Total sheduled hours: <span>{totalShiftHours}</span>
        </li>
        <li>
          Total clocked hours: <span>{totalClockedHours}</span>
        </li>
      </ul>
      {isModalOpen && (
        <CreateShiftModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          reload={() => reload()}
          staffOpt={staffOpt}
          serviceUserOpt={serviceUserOpt}
          initialShift={initialShift}
          isCloning={isCloning}
        />
      )}
    </div>
  );
}
